import { useState, useEffect, useCallback } from 'react';
import { comprasService } from '../services';
import { useDebounce } from './useDebounce';

/**
 * Hook personalizado para manejar todas las operaciones de órdenes de compra
 */
export const useCompras = () => {
  // Estados principales
  const [ordenes, setOrdenes] = useState([]);
  const [ordenesOriginales, setOrdenesOriginales] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [proveedorFilter, setProveedorFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Debounce para búsqueda
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  /**
   * Transformar datos de la API al formato local
   */
  const transformOrdenFromAPI = useCallback((orden) => ({
    id: orden.id,
    order_number: orden.order_number || '',
    supplier_id: orden.supplier_id,
    supplier_name: orden.supplier_name || orden.supplier?.name || '',
    order_date: orden.order_date,
    expected_delivery_date: orden.expected_delivery_date,
    delivery_date: orden.delivery_date,
    status: orden.status || 'DRAFT',
    priority: orden.priority || 'NORMAL',
    subtotal: orden.subtotal || 0,
    tax_amount: orden.tax_amount || 0,
    tax_rate: orden.tax_rate || 0,
    shipping_cost: orden.shipping_cost || 0,
    discount_amount: orden.discount_amount || 0,
    total_amount: orden.total_amount || 0,
    items_count: orden.items_count || 0,
    notes: orden.notes || '',
    supplier_notes: orden.supplier_notes || '',
    terms_and_conditions: orden.terms_and_conditions || '',
    shipping_address: orden.shipping_address || '',
    sent_date: orden.sent_date,
    sent_by: orden.sent_by,
    confirmation_date: orden.confirmation_date,
    approved_date: orden.approved_date,
    approved_by: orden.approved_by,
    created_by: orden.created_by,
    is_fully_received: orden.is_fully_received || false,
    items: orden.items || [],
    created_at: orden.created_at,
    updated_at: orden.updated_at
  }), []);

  /**
   * Cargar órdenes de compra
   */
  const cargarOrdenes = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        skip: 0,
        limit: 100
      };

      // Aplicar filtros
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (proveedorFilter) {
        params.supplier_id = proveedorFilter;
      }
      if (dateFromFilter) {
        params.date_from = dateFromFilter;
      }
      if (dateToFilter) {
        params.date_to = dateToFilter;
      }
      if (priorityFilter) {
        params.priority = priorityFilter;
      }

      console.log('Cargando órdenes de compra con parámetros:', params);
      const result = await comprasService.getOrdenes(params);
      console.log('Resultado de la API:', result);

      if (result.success) {
        const ordenesTransformadas = (result.data || []).map(transformOrdenFromAPI);
        setOrdenes(ordenesTransformadas);
        setOrdenesOriginales(ordenesTransformadas);
        setIsInitialized(true);
      } else {
        setError(result.error || result.message || 'Error al cargar órdenes de compra');
        setOrdenes([]);
        setOrdenesOriginales([]);
      }
    } catch (error) {
      setError('Error de conexión al cargar órdenes de compra');
      console.error('Error loading purchase orders:', error);
      setOrdenes([]);
      setOrdenesOriginales([]);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, proveedorFilter, dateFromFilter, dateToFilter, priorityFilter, transformOrdenFromAPI]);

  /**
   * Realizar búsqueda con debounce
   */
  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      // Si no hay búsqueda, cargar todas las órdenes
      await cargarOrdenes();
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const searchParams = {
        skip: 0,
        limit: 100,
        search: query
      };
      
      // Aplicar filtros existentes
      if (statusFilter) searchParams.status = statusFilter;
      if (proveedorFilter) searchParams.supplier_id = proveedorFilter;
      if (dateFromFilter) searchParams.date_from = dateFromFilter;
      if (dateToFilter) searchParams.date_to = dateToFilter;
      if (priorityFilter) searchParams.priority = priorityFilter;
      
      console.log('Realizando búsqueda con parámetros:', searchParams);
      const result = await comprasService.getOrdenes(searchParams);
      
      if (result.success) {
        const ordenesData = (result.data || []).map(transformOrdenFromAPI);
        setOrdenes(ordenesData);
        setOrdenesOriginales(ordenesData);
      } else {
        setError(result.error || result.message || 'Error en la búsqueda');
        setOrdenes([]);
        setOrdenesOriginales([]);
      }
    } catch (err) {
      setError('Error de conexión en la búsqueda');
      console.error('Error searching purchase orders:', err);
      setOrdenes([]);
      setOrdenesOriginales([]);
    } finally {
      setIsLoading(false);
    }
  }, [cargarOrdenes, statusFilter, proveedorFilter, dateFromFilter, dateToFilter, priorityFilter, transformOrdenFromAPI]);

  /**
   * Crear nueva orden de compra
   */
  const crearOrden = useCallback(async (ordenData) => {
    try {
      setError(null);
      const result = await comprasService.crearOrden(ordenData);

      if (result.success) {
        await cargarOrdenes(); // Recargar lista
        return {
          success: true,
          data: result.data,
          message: 'Orden de compra creada exitosamente'
        };
      } else {
        return {
          success: false,
          error: result.error || 'Error al crear orden de compra'
        };
      }
    } catch (error) {
      console.error('Error creating purchase order:', error);
      return {
        success: false,
        error: 'Error de conexión al crear orden de compra'
      };
    }
  }, [cargarOrdenes]);

  /**
   * Actualizar orden de compra
   */
  const actualizarOrden = useCallback(async (ordenId, ordenData) => {
    try {
      setError(null);
      const result = await comprasService.actualizarOrden(ordenId, ordenData);

      if (result.success) {
        await cargarOrdenes(); // Recargar lista
        return {
          success: true,
          data: result.data,
          message: 'Orden de compra actualizada exitosamente'
        };
      } else {
        return {
          success: false,
          error: result.error || 'Error al actualizar orden de compra'
        };
      }
    } catch (error) {
      console.error('Error updating purchase order:', error);
      return {
        success: false,
        error: 'Error de conexión al actualizar orden de compra'
      };
    }
  }, [cargarOrdenes]);

  /**
   * Enviar orden de compra por email
   */
  const enviarOrden = useCallback(async (ordenId, emailData = {}) => {
    try {
      setError(null);
      const result = await comprasService.enviarOrden(ordenId, emailData);

      if (result.success) {
        await cargarOrdenes(); // Recargar para actualizar estado
        return {
          success: true,
          message: 'Orden de compra enviada exitosamente'
        };
      } else {
        setError(result.error || 'Error al enviar orden de compra');
        return {
          success: false,
          error: result.error || 'Error al enviar orden de compra'
        };
      }
    } catch (error) {
      const errorMessage = 'Error de conexión al enviar orden de compra';
      setError(errorMessage);
      console.error('Error sending purchase order:', error);
      return {
        success: false,
        error: errorMessage
      };
    }
  }, [cargarOrdenes]);

  /**
   * Aprobar orden de compra
   */
  const aprobarOrden = useCallback(async (ordenId, approvalData = {}) => {
    try {
      setError(null);
      const result = await comprasService.aprobarOrden(ordenId, approvalData);

      if (result.success) {
        await cargarOrdenes(); // Recargar para actualizar estado
        return {
          success: true,
          message: 'Orden de compra aprobada exitosamente'
        };
      } else {
        setError(result.error || 'Error al aprobar orden de compra');
        return {
          success: false,
          error: result.error || 'Error al aprobar orden de compra'
        };
      }
    } catch (error) {
      const errorMessage = 'Error de conexión al aprobar orden de compra';
      setError(errorMessage);
      console.error('Error approving purchase order:', error);
      return {
        success: false,
        error: errorMessage
      };
    }
  }, [cargarOrdenes]);

  /**
   * Recibir productos de orden de compra
   */
  const recibirOrden = useCallback(async (ordenId, receiptData) => {
    try {
      setError(null);
      const result = await comprasService.recibirOrden(ordenId, receiptData);

      if (result.success) {
        await cargarOrdenes(); // Recargar para actualizar estado
        return {
          success: true,
          data: result.data,
          message: 'Productos recibidos exitosamente'
        };
      } else {
        return {
          success: false,
          error: result.error || result.message || 'Error al recibir productos'
        };
      }
    } catch (error) {
      console.error('Error receiving purchase order:', error);
      return {
        success: false,
        error: error?.message || 'Error de conexión al recibir productos'
      };
    }
  }, [cargarOrdenes]);

  /**
   * Cancelar orden de compra
   */
  const cancelarOrden = useCallback(async (ordenId) => {
    try {
      setError(null);
      const result = await comprasService.cancelarOrden(ordenId);

      if (result.success) {
        await cargarOrdenes(); // Recargar para actualizar estado
        return {
          success: true,
          message: 'Orden de compra cancelada exitosamente'
        };
      } else {
        setError(result.error || 'Error al cancelar orden de compra');
        return {
          success: false,
          error: result.error || 'Error al cancelar orden de compra'
        };
      }
    } catch (error) {
      const errorMessage = 'Error de conexión al cancelar orden de compra';
      setError(errorMessage);
      console.error('Error cancelling purchase order:', error);
      return {
        success: false,
        error: errorMessage
      };
    }
  }, [cargarOrdenes]);

  /**
   * Obtener orden de compra por ID
   */
  const getOrdenById = useCallback(async (ordenId) => {
    try {
      setError(null);
      const result = await comprasService.getOrdenById(ordenId);

      if (result.success) {
        return {
          success: true,
          data: transformOrdenFromAPI(result.data)
        };
      } else {
        return {
          success: false,
          error: result.error || 'Orden de compra no encontrada'
        };
      }
    } catch (error) {
      console.error('Error getting purchase order:', error);
      return {
        success: false,
        error: 'Error de conexión al obtener orden de compra'
      };
    }
  }, [transformOrdenFromAPI]);

  /**
   * Buscar órdenes de compra (inmediata)
   */
  const buscarOrdenes = useCallback((query) => {
    console.log('Buscando órdenes con query:', query);
    setSearchQuery(query);
  }, []);

  /**
   * Limpiar búsqueda y filtros
   */
  const limpiarBusqueda = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('');
    setProveedorFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setPriorityFilter('');
  }, []);

  /**
   * Duplicar orden de compra
   */
  const duplicarOrden = useCallback(async (ordenId, newData = {}) => {
    try {
      setError(null);
      const result = await comprasService.duplicarOrden(ordenId, newData);

      if (result.success) {
        await cargarOrdenes(); // Recargar lista
        return {
          success: true,
          data: result.data,
          message: 'Orden de compra duplicada exitosamente'
        };
      } else {
        return {
          success: false,
          error: result.error || 'Error al duplicar orden de compra'
        };
      }
    } catch (error) {
      console.error('Error duplicating purchase order:', error);
      return {
        success: false,
        error: 'Error de conexión al duplicar orden de compra'
      };
    }
  }, [cargarOrdenes]);

  /**
   * Marcar orden como urgente
   */
  const marcarComoUrgente = useCallback(async (ordenId) => {
    try {
      setError(null);
      const result = await comprasService.actualizarOrden(ordenId, { priority: 'URGENT' });

      if (result.success) {
        await cargarOrdenes(); // Recargar para actualizar estado
        return {
          success: true,
          message: 'Orden marcada como urgente'
        };
      } else {
        setError(result.error || 'Error al marcar orden como urgente');
        return {
          success: false,
          error: result.error || 'Error al marcar orden como urgente'
        };
      }
    } catch (error) {
      const errorMessage = 'Error de conexión al marcar orden como urgente';
      setError(errorMessage);
      console.error('Error marking order as urgent:', error);
      return {
        success: false,
        error: errorMessage
      };
    }
  }, [cargarOrdenes]);

  /**
   * Obtener estadísticas de órdenes de compra
   */
  const getEstadisticas = useCallback(() => {
    const stats = {
      total: ordenes.length,
      pendientes: ordenes.filter(orden => orden.status === 'PENDING').length,
      enviadas: ordenes.filter(orden => orden.status === 'SENT').length,
      confirmadas: ordenes.filter(orden => orden.status === 'CONFIRMED').length,
      recibidas: ordenes.filter(orden => orden.status === 'RECEIVED').length,
      canceladas: ordenes.filter(orden => orden.status === 'CANCELLED').length,
      montoTotal: ordenes.reduce((sum, orden) => sum + (orden.total_amount || 0), 0),
      
      // Por prioridad
      urgentes: ordenes.filter(orden => orden.priority === 'URGENT').length,
      altas: ordenes.filter(orden => orden.priority === 'HIGH').length,
      
      // Órdenes que requieren atención
      requierenAtencion: ordenes.filter(orden => 
        orden.status === 'PENDING' || orden.status === 'SENT' || orden.priority === 'URGENT'
      ).length
    };

    return stats;
  }, [ordenes]);

  /**
   * Obtener órdenes que requieren atención
   */
  const getOrdenesRequierenAtencion = useCallback(() => {
    return ordenes.filter(orden => {
      // Órdenes urgentes
      if (orden.priority === 'URGENT') return true;
      
      // Órdenes pendientes por mucho tiempo
      const daysSincePending = orden.status === 'PENDING' && 
        (new Date() - new Date(orden.order_date)) / (1000 * 60 * 60 * 24) > 7;
      
      // Órdenes enviadas sin confirmación por mucho tiempo
      const daysSinceSent = orden.status === 'SENT' && orden.sent_date &&
        (new Date() - new Date(orden.sent_date)) / (1000 * 60 * 60 * 24) > 3;
      
      return daysSincePending || daysSinceSent;
    });
  }, [ordenes]);

  /**
   * Recargar órdenes de compra
   */
  const recargarOrdenes = useCallback(() => {
    setIsInitialized(false);
    cargarOrdenes();
  }, [cargarOrdenes]);

  /**
   * Limpiar errores
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Efectos
  useEffect(() => {
    if (!isInitialized) {
      cargarOrdenes();
    }
  }, [cargarOrdenes, isInitialized]);

  // Efecto para búsqueda con debounce
  useEffect(() => {
    if (isInitialized) {
      performSearch(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, performSearch, isInitialized]);

  // Efecto para recarga cuando cambian los filtros
  useEffect(() => {
    if (isInitialized) {
      if (searchQuery.trim()) {
        // Si hay búsqueda activa, realizar búsqueda con nuevos filtros
        performSearch(searchQuery);
      } else {
        // Si no hay búsqueda, cargar con filtros
        cargarOrdenes();
      }
    }
  }, [statusFilter, proveedorFilter, dateFromFilter, dateToFilter, priorityFilter, cargarOrdenes, performSearch, searchQuery, isInitialized]);

  return {
    // Datos
    ordenes,
    searchQuery,
    statusFilter,
    proveedorFilter,
    dateFromFilter,
    dateToFilter,
    priorityFilter,
    
    // Estados
    isLoading,
    error,
    isInitialized,
    
    // Funciones principales
    crearOrden,
    actualizarOrden,
    enviarOrden,
    aprobarOrden,
    recibirOrden,
    cancelarOrden,
    getOrdenById,
    duplicarOrden,
    marcarComoUrgente,
    performSearch,
    
    // Búsqueda y filtros
    buscarOrdenes,
    setStatusFilter,
    setProveedorFilter,
    setDateFromFilter,
    setDateToFilter,
    setPriorityFilter,
    limpiarBusqueda,
    
    // Estadísticas
    getEstadisticas,
    getOrdenesRequierenAtencion,
    
    // Utilidades
    recargarOrdenes,
    clearError
  };
};

export default useCompras;
